/**
 * Work Document Uploader
 * Drag-and-drop file upload for work documentation (pay stubs, timesheets, etc.)
 * Trust Marketplace - Work Documentation System
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Upload,
  File,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  Eye
} from 'lucide-react';

interface WorkDocumentUploaderProps {
  workActivityLogId: number;
  enrollmentId: number;
  onUploadComplete?: () => void;
}

interface UploadedDocument {
  id: number;
  file_name: string;
  document_type: string;
  file_size_bytes: number;
  uploaded_at: string;
  verification_status: string;
  is_primary: boolean;
}

const DOCUMENT_TYPES = [
  { value: 'PAY_STUB', label: 'Pay Stub', icon: '💰' },
  { value: 'TIMESHEET', label: 'Timesheet', icon: '⏰' },
  { value: 'EMPLOYER_LETTER', label: 'Employer Letter', icon: '✉️' },
  { value: 'EDUCATION_TRANSCRIPT', label: 'Education Transcript', icon: '🎓' },
  { value: 'VOLUNTEER_LETTER', label: 'Volunteer Letter', icon: '❤️' },
  { value: 'TRAINING_CERTIFICATE', label: 'Training Certificate', icon: '📜' },
  { value: 'SCHOOL_ENROLLMENT', label: 'School Enrollment Proof', icon: '🏫' },
  { value: 'SELF_EMPLOYMENT', label: 'Self-Employment Records', icon: '💼' },
  { value: 'CAREGIVER_AFFIDAVIT', label: 'Caregiver Affidavit', icon: '👨‍👩‍👧' },
  { value: 'OTHER', label: 'Other', icon: '📄' }
];

const MAX_FILE_SIZE_MB = 10;
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const WorkDocumentUploader: React.FC<WorkDocumentUploaderProps> = ({
  workActivityLogId,
  enrollmentId,
  onUploadComplete
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [files, setFiles] = useState<File[]>([]);
  const [documentTypes, setDocumentTypes] = useState<{ [key: string]: string }>({});
  const [primaryDoc, setPrimaryDoc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Load existing documents on mount
  React.useEffect(() => {
    fetchUploadedDocuments();
  }, [workActivityLogId]);

  const fetchUploadedDocuments = async () => {
    try {
      const url = getWeaveinApiUrl(`/api/work-compliance/documents?work_activity_log_id=${workActivityLogId}`);

      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        setUploadedDocs(data.documents || []);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = (newFiles: File[]) => {
    setError(null);

    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];

    newFiles.forEach(file => {
      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errors.push(`${file.name}: File too large (max ${MAX_FILE_SIZE_MB}MB)`);
        return;
      }

      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type (PDF, JPG, PNG, DOC only)`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('; '));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);

      // Auto-select document types for new files
      const newTypes = { ...documentTypes };
      validFiles.forEach(file => {
        if (!newTypes[file.name]) {
          // Try to guess document type from filename
          const nameLower = file.name.toLowerCase();
          if (nameLower.includes('pay') || nameLower.includes('stub')) {
            newTypes[file.name] = 'PAY_STUB';
          } else if (nameLower.includes('timesheet') || nameLower.includes('hours')) {
            newTypes[file.name] = 'TIMESHEET';
          } else if (nameLower.includes('letter')) {
            newTypes[file.name] = 'EMPLOYER_LETTER';
          } else if (nameLower.includes('transcript')) {
            newTypes[file.name] = 'EDUCATION_TRANSCRIPT';
          } else {
            newTypes[file.name] = 'OTHER';
          }
        }
      });
      setDocumentTypes(newTypes);

      // Set first file as primary if none selected
      if (!primaryDoc && validFiles.length > 0) {
        setPrimaryDoc(validFiles[0].name);
      }
    }
  };

  const removeFile = (fileName: string) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    const newTypes = { ...documentTypes };
    delete newTypes[fileName];
    setDocumentTypes(newTypes);

    if (primaryDoc === fileName) {
      setPrimaryDoc(files.length > 1 ? files.find(f => f.name !== fileName)?.name || null : null);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const url = getWeaveinApiUrl('/api/work-compliance/documents/upload');

      // Upload files one by one
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('work_activity_log_id', workActivityLogId.toString());
        formData.append('enrollment_id', enrollmentId.toString());
        formData.append('document_type', documentTypes[file.name] || 'OTHER');
        formData.append('is_primary', (primaryDoc === file.name).toString());

        const response = await authFetch(url, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to upload ${file.name}`);
        }
      }

      // Success - clear form and refresh uploaded docs
      setFiles([]);
      setDocumentTypes({});
      setPrimaryDoc(null);
      await fetchUploadedDocuments();

      if (onUploadComplete) {
        onUploadComplete();
      }

      alert(`✅ Successfully uploaded ${files.length} document(s)!`);

    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId: number) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const url = getWeaveinApiUrl(`/api/work-compliance/documents/${docId}`);

      const response = await authFetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      await fetchUploadedDocuments();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return <CheckCircle2 className="text-green-600" size={16} />;
      case 'REJECTED':
        return <XCircle className="text-red-600" size={16} />;
      case 'PENDING':
        return <AlertCircle className="text-yellow-600" size={16} />;
      default:
        return <File className="text-gray-600" size={16} />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        {!tabbed && <h2 className="text-2xl font-bold">{t('workcompliance.upload_work_documentation', 'Upload Work Documentation')}</h2>}
        <p className="text-gray-600 mt-1">
          Upload supporting documents for your work activity submission (pay stubs, timesheets, letters)
        </p>
      </div>

      {error && (
        <Card className="p-4 border-l-4 border-red-500 bg-red-50">
          <div className="flex items-center text-red-800">
            <AlertCircle className="mr-2" size={20} />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Upload Area */}
      <Card className="p-6">
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-lg font-semibold mb-2">{t('workcompliance.drag_and_drop_files_here', 'Drag and drop files here')}</p>
          <p className="text-sm text-gray-600 mb-4">
            or click to browse (PDF, JPG, PNG, DOC - max {MAX_FILE_SIZE_MB}MB)
          </p>
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileInput}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 cursor-pointer"
          >
            {t('workcompliance.select_files', 'Select Files')}
          </label>
        </div>
      </Card>

      {/* Selected Files */}
      {files.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Selected Files ({files.length})</h3>
          <div className="space-y-3">
            {files.map((file) => (
              <div key={file.name} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <FileText className="text-blue-600" size={24} />
                    <div className="flex-1">
                      <p className="font-semibold">{file.name}</p>
                      <p className="text-sm text-gray-600">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(file.name)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('workcompliance.document_type', 'Document Type')}</label>
                    <select
                      value={documentTypes[file.name] || 'OTHER'}
                      onChange={(e) => setDocumentTypes(prev => ({
                        ...prev,
                        [file.name]: e.target.value
                      }))}
                      className="w-full p-2 border rounded"
                    >
                      {DOCUMENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={primaryDoc === file.name}
                        onChange={(e) => setPrimaryDoc(e.target.checked ? file.name : null)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t('workcompliance.primary_document', 'Primary Document')}</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => {
                setFiles([]);
                setDocumentTypes({});
                setPrimaryDoc(null);
              }}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              {t('workcompliance.clear_all', 'Clear All')}
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <LoadingSpinner size="small" />
                  <span>{t('workcompliance.uploading', 'Uploading...')}</span>
                </>
              ) : (
                <>
                  <Upload size={20} />
                  <span>Upload {files.length} Document(s)</span>
                </>
              )}
            </button>
          </div>
        </Card>
      )}

      {/* Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            Uploaded Documents ({uploadedDocs.length})
          </h3>
          <div className="space-y-2">
            {uploadedDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 flex-1">
                  {getStatusIcon(doc.verification_status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{doc.file_name}</p>
                      {doc.is_primary && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs">{t('workcompliance.primary', 'Primary')}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{DOCUMENT_TYPES.find(dt => dt.value === doc.document_type)?.label}</span>
                      <span>{formatFileSize(doc.file_size_bytes)}</span>
                      <span>{fmt.short(doc.uploaded_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge className={
                    doc.verification_status === 'VERIFIED'
                      ? 'bg-green-100 text-green-800'
                      : doc.verification_status === 'REJECTED'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }>
                    {doc.verification_status}
                  </Badge>
                  <button
                    onClick={() => deleteDocument(doc.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title={t('workcompliance.delete_document', 'Delete document')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Info Box */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h4 className="font-semibold mb-2">📋 Document Guidelines</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Upload clear, legible copies of your documents</li>
          <li>• Pay stubs must show employer name, dates, and hours worked</li>
          <li>• Timesheets should be signed/verified by supervisor</li>
          <li>• Education documents must show enrollment status and dates</li>
          <li>• All documents will be reviewed by your CBO within 3 business days</li>
          <li>• Mark your most important document as "Primary"</li>
        </ul>
      </Card>
    </div>
  );
};

export default WorkDocumentUploader;
